import { ClipboardCheck, GraduationCap, Leaf } from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { Reveal } from '../Reveal';
import { CheckList } from '../compass/shared';

// Section 8 — ISO and structured compliance. Certification stays with the
// independent certification body — Foresee supports the process.
export const ForeseeIso = ({ sp }) => (
  <section aria-labelledby="foresee-iso-heading" data-testid="foresee-iso" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <SectionHeader
            align="left"
            eyebrow="ISO & Compliance"
            title="Keep structured compliance activity connected"
            description={sp.isoCopy}
            testid="foresee-iso-header"
          />
          <p className="mt-6 border-l-2 border-indigo-500 pl-4 text-sm font-medium leading-relaxed text-slate-600" data-testid="foresee-iso-disclaimer">
            Foresee supports audit preparation and structured compliance management. Certification decisions remain with the relevant independent certification body.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-xl border border-slate-200 bg-mist p-7 sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-600 text-white">
              <ClipboardCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="mt-6">
              <CheckList items={sp.isoPoints || []} testid="foresee-iso-points" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// Section 9 — training and workforce competence. Records visibility, not a
// guarantee of competence.
export const ForeseeTraining = ({ sp }) => (
  <section aria-labelledby="foresee-training-heading" data-testid="foresee-training" className="bg-mist py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal className="order-last lg:order-first">
          <div className="rounded-xl border border-slate-200 bg-white p-7 sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy-900 text-gold-500">
              <GraduationCap className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="mt-6">
              <CheckList items={sp.trainingPoints || []} testid="foresee-training-points" />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <SectionHeader
            align="left"
            eyebrow="Training & Competence"
            title="Maintain clearer training and certification visibility"
            testid="foresee-training-header"
          />
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Keep employee training records, certificates and expiry dates connected to the workforce and compliance information management already uses — so upcoming expiries and missing requirements surface before they become a problem.
          </p>
          <p className="mt-5 border-l-2 border-gold-500 pl-4 text-sm font-medium leading-relaxed text-slate-600">
            Recording a certificate supports visibility — it does not by itself guarantee competence or regulatory compliance.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

// Section 12 — ESG and environmental visibility.
export const ForeseeEsg = ({ sp }) => (
  <section aria-labelledby="foresee-esg-heading" data-testid="foresee-esg" className="bg-mist py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <SectionHeader
            align="left"
            eyebrow="ESG & Environmental"
            title="Connect environmental and sustainability activity"
            testid="foresee-esg-header"
          />
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Foresee can support configured environmental and ESG recording alongside the same reporting and action workflows — keeping improvement activity visible and assigned.
          </p>
          <p className="mt-5 border-l-2 border-indigo-500 pl-4 text-sm font-medium leading-relaxed text-slate-600">
            Foresee supports the recording and review of configured information — it is not carbon accounting or statutory ESG reporting software.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-xl border border-slate-200 bg-white p-7 sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <Leaf className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="mt-6">
              <CheckList items={sp.esgPoints || []} testid="foresee-esg-points" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
